import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Input,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

/*  Shared  */
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { Notify } from 'application/Shared/Helpers';

/* Hooks */
import {
    useDeleteTenantApplicationLinkContract,
    useFetchTenantApplicationLinkContract,
    usePostTenantApplicationLinkContract,
    usePutTenantApplicationLinkContract
} from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLinkContract';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const ConfigurationTitle = ({ type }) => {
    const { t } = useTranslation();
    if (type === 'month') {
        return (
            <Typography
                color="textSecondary"
                style={{
                    fontWeight: 600,
                    marginBottom: '8px'
                }}
            >
                {t('ApplicationContractManagement.monthlyConfiguration')}
            </Typography>
        );
    }

    if (type === 'renew') {
        return (
            <Typography
                color="textSecondary"
                style={{
                    fontWeight: 600,
                    marginBottom: '8px'
                }}
            >
                {t('ApplicationContractManagement.renewConfiguration')}
            </Typography>
        );
    }

    return null;
};

const DatePickerLabel = ({ type }) => {
    const { t } = useTranslation();
    if (type === 'month') {
        return (
            <FormLabel
                style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    marginRight: '16px'
                }}
            >
                {t('ApplicationContractManagement.renewalDateDayMonth')}
                <TooltipQ
                    text={t(
                        'Tooltips.contract_management_monthly_renewal_date'
                    )}
                />
            </FormLabel>
        );
    }

    if (type === 'renew') {
        return (
            <FormLabel
                style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    marginRight: '16px'
                }}
            >
                {t('ApplicationContractManagement.renewalDate')}
                <TooltipQ
                    text={t('Tooltips.contract_management_renew_renewal_date')}
                />
            </FormLabel>
        );
    }

    return null;
};

const getValidUsers = (users) => {
    if (Number.isNaN(users) || users < 1) {
        return 0;
    }
    return users;
};

const getValidDaysBefore = (daysBefore) => {
    if (Number.isNaN(daysBefore)) {
        return 7;
    }
    if (daysBefore > 28) {
        return 28;
    }
    if (daysBefore < 1) {
        return 1;
    }
    return daysBefore;
};

const clear = ({ currentContract, defaultContract, setCurrentContract }) => {
    // If there is a contractId, keep it, otherwise you won't be able to delete the contract
    // Also, let the user change the enable checkbox
    setCurrentContract({
        ...defaultContract,
        enabled: currentContract.enabled,
        id: currentContract.id
    });
};

const save = async ({
    contractExists,
    currentContract,
    deleteTenantApplicationLinkContract,
    postTenantApplicationLinkContract,
    putTenantApplicationLinkContract,
    refetchContract,
    reloadParentFn,
    t
}) => {
    const contractDetails = { ...currentContract };
    const contractEnabled = currentContract.enabled;
    delete contractDetails.enabled;

    const postData = contractEnabled && !contractExists;
    const putData = contractEnabled && contractExists;
    const deleteData = !contractEnabled && contractExists;

    if (putData) {
        await putTenantApplicationLinkContract();
        refetchContract();
    }

    if (postData) {
        await postTenantApplicationLinkContract();
        refetchContract();
    }

    if (deleteData) {
        await deleteTenantApplicationLinkContract();
        refetchContract();
    }

    if (!contractEnabled && !contractExists) {
        Notify.success(t('ApplicationContractManagement.successSave'));
    }

    reloadParentFn();
};

const defaultContract = {
    alerting: true,
    daysBefore: 7,
    enabled: false,
    mrr: 0,
    notes: '',
    nrc: 0,
    renewalDate: new Date(),
    tcv: 0,
    type: 'month',
    users: 0
};

export const ContractManagement = ({
    applicationId,
    managedByPartner,
    reloadParentFn
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();

    const { contract, contractExists, loading, refetchContract } =
        useFetchTenantApplicationLinkContract({
            applicationId,
            clientId,
            defaultContract
        });

    const [currentContract, setCurrentContract] = useState(contract);

    const { postTenantApplicationLinkContract, saving: postSaving } =
        usePostTenantApplicationLinkContract({
            clientId,
            contract: currentContract,
            t
        });

    const { putTenantApplicationLinkContract, saving: putSaving } =
        usePutTenantApplicationLinkContract({
            clientId,
            contract: currentContract,
            t
        });

    const { deleteTenantApplicationLinkContract, saving: deleteSaving } =
        useDeleteTenantApplicationLinkContract({
            clientId,
            contractId: currentContract.id,
            t
        });

    const saving = postSaving || putSaving || deleteSaving;

    useEffect(() => {
        setCurrentContract(contract);
    }, [contract]);

    if (loading) {
        return (
            <Box
                data-trackid="managedTableRenderLoading"
                display="flex"
                justifyContent="center"
                width="100%"
                className="no-print"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={currentContract.enabled}
                                onChange={() =>
                                    setCurrentContract({
                                        ...currentContract,
                                        enabled: !currentContract.enabled
                                    })
                                }
                                color="primary"
                            />
                        }
                        label={
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'inline-flex'
                                }}
                            >
                                {t('ApplicationContractManagement.enabled')}
                                <TooltipQ
                                    text={t(
                                        'Tooltips.contract_management_enabled'
                                    )}
                                />
                            </Box>
                        }
                    />
                    {currentContract.enabled && (
                        <Box mt="16px">
                            <FormLabel>
                                <Typography fontWeight={600}>
                                    {t(
                                        'ApplicationContractManagement.contractType'
                                    )}
                                </Typography>
                            </FormLabel>
                            <RadioGroup
                                row
                                value={currentContract.type}
                                onChange={(e) =>
                                    setCurrentContract({
                                        ...currentContract,
                                        type: e.currentTarget.value
                                    })
                                }
                            >
                                <FormControlLabel
                                    value="month"
                                    control={<Radio />}
                                    label={t(
                                        'ApplicationContractManagement.monthToMonth'
                                    )}
                                />
                                <FormControlLabel
                                    value="renew"
                                    control={<Radio />}
                                    label={t(
                                        'ApplicationContractManagement.renewDate'
                                    )}
                                />
                            </RadioGroup>

                            <hr />
                            <Grid container>
                                <Grid item xs={12}>
                                    <ConfigurationTitle
                                        type={currentContract.type}
                                    />
                                    <Box display="flex" alignItems="center">
                                        <DatePickerLabel
                                            type={currentContract.type}
                                        />
                                        <DatePicker
                                            value={
                                                new Date(
                                                    currentContract.renewalDate
                                                )
                                            }
                                            views={
                                                currentContract.type === 'renew'
                                                    ? ['year', 'month', 'day']
                                                    : ['day']
                                            }
                                            onChange={(e) =>
                                                setCurrentContract({
                                                    ...currentContract,
                                                    renewalDate: new Date(e)
                                                })
                                            }
                                        />
                                    </Box>
                                    <Box mt="16px">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        currentContract.alerting
                                                    }
                                                    onChange={() =>
                                                        setCurrentContract({
                                                            ...currentContract,
                                                            alerting:
                                                                !currentContract.alerting
                                                        })
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box
                                                    sx={{
                                                        alignItems: 'center',
                                                        display: 'inline-flex'
                                                    }}
                                                >
                                                    {managedByPartner
                                                        ? t(
                                                              'ApplicationContractManagement.alertingPsa'
                                                          )
                                                        : t(
                                                              'ApplicationContractManagement.alerting'
                                                          )}
                                                    {currentContract.type ===
                                                        'month' && (
                                                        <TooltipQ
                                                            text={t(
                                                                'Tooltips.contract_management_alerting_monthly'
                                                            )}
                                                        />
                                                    )}
                                                    {currentContract.type ===
                                                        'renew' && (
                                                        <TooltipQ
                                                            text={t(
                                                                'Tooltips.contract_management_alerting_renew'
                                                            )}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </Box>
                                    {currentContract.alerting &&
                                        currentContract.type === 'month' && (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <FormLabel
                                                    style={{
                                                        alignItems: 'center',
                                                        display: 'inline-flex',
                                                        marginRight: '16px'
                                                    }}
                                                >
                                                    {t(
                                                        'ApplicationContractManagement.daysAlert'
                                                    )}
                                                    <TooltipQ
                                                        text={t(
                                                            'Tooltips.contract_management_alerting_days'
                                                        )}
                                                    />
                                                </FormLabel>
                                                <TextField
                                                    autoFocus={false}
                                                    type="number"
                                                    inputProps={{
                                                        max: 30,
                                                        min: 1
                                                    }}
                                                    onChange={(e) =>
                                                        setCurrentContract({
                                                            ...currentContract,
                                                            daysBefore:
                                                                getValidDaysBefore(
                                                                    e.target
                                                                        .value
                                                                )
                                                        })
                                                    }
                                                    autoComplete="off"
                                                    value={
                                                        currentContract.daysBefore
                                                    }
                                                />
                                            </Box>
                                        )}
                                </Grid>
                            </Grid>
                            <hr />
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: '8px'
                                }}
                            >
                                {t(
                                    'ApplicationContractManagement.contractDetails'
                                )}
                            </Typography>
                            <Grid container>
                                <Grid item xs={3}>
                                    <FormLabel
                                        sx={{
                                            alignItems: 'center',
                                            display: 'inline-flex'
                                        }}
                                    >
                                        {t('ApplicationContractManagement.nrc')}
                                        <TooltipQ
                                            text={t(
                                                'Tooltips.contract_management_nrc'
                                            )}
                                        />
                                    </FormLabel>

                                    <br />
                                    <FormControl
                                        style={{ width: '150px' }}
                                        variant="standard"
                                    >
                                        <Input
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    $
                                                </InputAdornment>
                                            }
                                            value={currentContract.nrc}
                                            onChange={(e) =>
                                                setCurrentContract({
                                                    ...currentContract,
                                                    nrc: e.target.value
                                                })
                                            }
                                            type="number"
                                            inputProps={{ min: 0 }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                    <FormLabel
                                        sx={{
                                            alignItems: 'center',
                                            display: 'inline-flex'
                                        }}
                                    >
                                        {t('ApplicationContractManagement.mrr')}
                                        <TooltipQ
                                            text={t(
                                                'Tooltips.contract_management_mrr'
                                            )}
                                        />
                                    </FormLabel>
                                    <br />
                                    <FormControl
                                        style={{ width: '150px' }}
                                        variant="standard"
                                    >
                                        <Input
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    $
                                                </InputAdornment>
                                            }
                                            value={currentContract.mrr}
                                            onChange={(e) =>
                                                setCurrentContract({
                                                    ...currentContract,
                                                    mrr: e.target.value
                                                })
                                            }
                                            type="number"
                                            inputProps={{ min: 0 }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                    <FormLabel
                                        sx={{
                                            alignItems: 'center',
                                            display: 'inline-flex'
                                        }}
                                    >
                                        {t('ApplicationContractManagement.tcv')}
                                        <TooltipQ
                                            text={t(
                                                'Tooltips.contract_management_tcv'
                                            )}
                                        />
                                    </FormLabel>
                                    <br />
                                    <FormControl
                                        style={{ width: '150px' }}
                                        variant="standard"
                                    >
                                        <Input
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    $
                                                </InputAdornment>
                                            }
                                            value={currentContract.tcv}
                                            onChange={(e) =>
                                                setCurrentContract({
                                                    ...currentContract,
                                                    tcv: e.target.value
                                                })
                                            }
                                            type="number"
                                            inputProps={{ min: 0 }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                    <FormLabel
                                        sx={{
                                            alignItems: 'center',
                                            display: 'inline-flex'
                                        }}
                                    >
                                        {t(
                                            'ApplicationContractManagement.users'
                                        )}
                                        <TooltipQ
                                            text={t(
                                                'Tooltips.contract_management_users'
                                            )}
                                        />
                                    </FormLabel>
                                    <br />
                                    <TextField
                                        style={{ width: '150px' }}
                                        autoFocus={false}
                                        type="number"
                                        onChange={(e) =>
                                            setCurrentContract({
                                                ...currentContract,
                                                users: getValidUsers(
                                                    e.target.value
                                                )
                                            })
                                        }
                                        fullWidth
                                        autoComplete="off"
                                        value={currentContract.users}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                            </Grid>
                            <br />

                            <FormLabel
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    marginBottom: '8px'
                                }}
                            >
                                <Typography
                                    color="textSecondary"
                                    fontWeight={600}
                                >
                                    {t(
                                        'ApplicationContractManagement.contractNotes'
                                    )}
                                </Typography>
                                <TooltipQ
                                    text={t(
                                        'Tooltips.contract_management_notes'
                                    )}
                                />
                            </FormLabel>
                            <TextField
                                variant="outlined"
                                multiline
                                minRows={4}
                                autoFocus={false}
                                type="text"
                                onChange={(e) =>
                                    setCurrentContract({
                                        ...currentContract,
                                        notes: e.target.value
                                    })
                                }
                                fullWidth
                                autoComplete="off"
                                value={currentContract.notes}
                            />
                        </Box>
                    )}

                    <Grid item xs={12}>
                        <hr />
                        <Box display="flex" justifyContent="space-between">
                            <Button
                                data-trackid="admin_application_contractManagement_clear"
                                disabled={saving}
                                onClick={() =>
                                    clear({
                                        currentContract,
                                        defaultContract,
                                        setCurrentContract
                                    })
                                }
                                size="small"
                                variant="outlined"
                                color="primary"
                            >
                                {t('Common.clear')}
                            </Button>
                            <Button
                                data-trackid="admin_application_contractManagement_save"
                                disabled={saving}
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                    save({
                                        clientId,
                                        contractExists,
                                        currentContract,
                                        deleteTenantApplicationLinkContract,
                                        postTenantApplicationLinkContract,
                                        putTenantApplicationLinkContract,
                                        refetchContract,
                                        reloadParentFn,
                                        setCurrentContract,
                                        t
                                    })
                                }
                                style={{ marginLeft: '8px' }}
                            >
                                {t('Common.save_settings')}
                            </Button>
                        </Box>
                    </Grid>
                </FormGroup>
            </Grid>
        </Grid>
    );
};
